/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Component from 'flarum/common/Component';
import MobileTourCard from './MobileTourCard';
import loadDynLib from '../../common/helpers/loadDynLib';
import Button from 'flarum/common/components/Button';
import app from 'flarum/forum/app';
import type Mithril from 'mithril';

/**
 * Minimal shapes of the UMD globals loaded from a CDN by `loadDynLib`; neither
 * `swiper` nor `body-scroll-lock` is installed as a typed package here.
 */
interface SwiperInstance {
	destroy(): void;
}

interface SwiperOptions {
	effect?: string;
	grabCursor?: boolean;
	navigation?: {
		nextEl?: string;
		prevEl?: string;
	};
}

interface BodyScrollLock {
	disableBodyScroll(target: HTMLElement): void;
	clearAllBodyScrollLocks(): void;
}

declare const Swiper: new (selector: string, options?: SwiperOptions) => SwiperInstance;
declare const bodyScrollLock: BodyScrollLock;

export default class MobileTour extends Component<Record<string, never>> {
	protected cardIds!: string[];
	protected swiper?: SwiperInstance;
	protected disabled?: boolean;
	protected shouldNotSaveDisabled?: boolean;

	oninit(vnode: Mithril.Vnode<Record<string, never>, this>) {
		super.oninit(vnode);

		this.cardIds = ['user-profile', 'notifications', 'book-collections', 'news', 'reviews', 'discuss', 'reading-rounds', 'restart-tour'];

		const disableOnDesktop = (e: MediaQueryListEvent) => {
			if (!e.matches) {
				this.shouldNotSaveDisabled = true;
				this.complete();
			}
		};
		const isMobile = window.matchMedia('(max-width: 767.98px)');
		isMobile.addEventListener('change', disableOnDesktop);
	}

	oncreate(vnode: Mithril.VnodeDOM<Record<string, never>, this>) {
		super.oncreate(vnode);

		if (this.disabled) {
			return;
		}

		loadDynLib(['swiper', 'body-scroll-lock']).then(() => {
			this.swiper = new Swiper('.MobileTour-swiper', {
				effect: 'cards',
				grabCursor: true,
				navigation: {
					nextEl: '.swiper-button-next',
					prevEl: '.swiper-button-prev',
				},
			});

			bodyScrollLock.disableBodyScroll(document.body);

			$('#drawer').hide();
			$('.drawer-backdrop').hide();
		});
	}

	onremove(vnode: Mithril.VnodeDOM<Record<string, never>, this>) {
		super.onremove(vnode);

		this.complete();
	}

	complete() {
		$('#drawer').show();
		$('.drawer-backdrop').show();

		if (this.swiper) {
			this.swiper.destroy();
		}

		this.disabled = true;
		m.redraw();

		if (!this.shouldNotSaveDisabled) {
			app.session.user!.savePreferences({ homeTourDisabled: true });
		}

		delete this.shouldNotSaveDisabled;

		loadDynLib(['body-scroll-lock']).then(() => {
			bodyScrollLock.clearAllBodyScrollLocks();
		});
	}

	view(): Mithril.Children {
		if (this.disabled) {
			return null;
		}

		return (
			<div className="MobileTour">
				<div className="MobileTour-close App-backControl">
					<Button
						icon="fas fa-times"
						onclick={() => this.complete()}
						className="Button Button--icon Button--link"
						ariaLabel={app.translator.trans('core.lib.modal.close')}
					/>
				</div>
				<div className="MobileTour-swiper swiper">
					<div className="MobileTour-swiper-wrapper swiper-wrapper">
						{this.cardIds.map((cardId) => (
							<MobileTourCard cardId={cardId} complete={() => this.complete()} />
						))}
					</div>

					<div class="swiper-button-prev"></div>
					<div class="swiper-button-next"></div>
				</div>
			</div>
		);
	}
}
